import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import { I18n } from 'Locales';
import { Button } from 'Components';
import { Flow } from 'Repositories';
import { mockFunction } from 'Helpers';
import { FILE_SIZE_LIMITS } from 'Constants';
import { PDFIncomplete, Refresh } from 'Icons';
import { ColumnView, RowView } from 'Containers';

import './style.scss';

const handleUpload = async ({
  component_type = '',
  fieldId = '',
  file = {},
  id = '',
  isOffline = false,
  setErrorMessage = mockFunction,
  setLoadingImg = mockFunction,
  transient = {},
  setProgress = mockFunction,
  updateTransientProps = mockFunction,
  updateValue = mockFunction
}) => {
  setProgress(0);
  setLoadingImg(true);

  const selectedFile = file?.files[0];

  if (!selectedFile) {
    setLoadingImg(false);
    setErrorMessage(false);
    return;
  }

  if (!selectedFile.type.startsWith('image/')) {
    setLoadingImg(false);
    setErrorMessage('fileTypeError');
    return;
  }

  if (selectedFile.size >= FILE_SIZE_LIMITS.ATTACHMENTS) {
    setLoadingImg(false);
    setErrorMessage('sizeError');
    return;
  }

  const uploadedFile = { file: selectedFile, field_id: fieldId };
  setErrorMessage(false);

  if (!isOffline) {
    const reader = new FileReader();
    reader.readAsDataURL(selectedFile);

    reader.onload = () => {
      setProgress(0);
      const formData = new FormData();
      formData.append('file', selectedFile);

      let fakeProgress = 0;
      const interval = setInterval(() => {
        fakeProgress += Math.random() * 10;
        if (fakeProgress >= 95) fakeProgress = 90;
        setProgress(Math.floor(fakeProgress));
      }, 200);

      Flow.uploadAttachment(formData, {
        onUploadProgress: progressEvent => {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);

          setProgress(percentage);
        }
      })
        .then(res => {
          clearInterval(interval);
          setProgress(100);
          updateValue({
            component: component_type,
            value: res.data?.data?.id,
            valueName: selectedFile.name,
            content: URL.createObjectURL(selectedFile),
            id
          });
        })
        .catch(error => {
          clearInterval(interval);
          setProgress(0);
          setErrorMessage(error?.message);
        });
    };
  } else {
    updateValue({
      component: component_type,
      value: URL.createObjectURL(selectedFile),
      valueName: selectedFile.name,
      id
    });
  }

  updateTransientProps({ ...transient, uploadedFiles: { ...transient?.uploadedFiles, [id]: uploadedFile } });
  setLoadingImg(false);

  if (file) {
    file.value = '';
  }
};

const onButtonPress = ({
  component_type = '',
  file = {},
  id = '',
  onButtonClick = mockFunction,
  transient = {},
  updateTransientProps = mockFunction,
  updateValue = mockFunction
}) => {
  if (transient?.uploadedFiles?.[id]) {
    const updatedUploadedFiles = { ...transient?.uploadedFiles };
    delete updatedUploadedFiles[id];

    updateValue({ component: component_type, value: '', valueName: '', id: '' });
    updateTransientProps({ ...transient, uploadedFiles: updatedUploadedFiles });
  } else {
    onButtonClick(file);
  }
};

const onButtonClick = ref => ref && ref?.click();

const Upload = ({
  component_type = '',
  componentFlowData = {},
  fields = [],
  id = '',
  isOffline = false,
  name = '',
  required = false,
  shouldShowError = false,
  transient = {},
  updateTransientProps = mockFunction,
  updateValue = mockFunction
}) => {
  const uploadInputRef = useRef(null);
  const file = uploadInputRef?.current;
  const [progress, setProgress] = useState(0);
  const [loadingImg, setLoadingImg] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  const fieldId = fields?.[0]?.id;
  const { uploadedFiles = {} } = transient;
  const { valueName = '' } = componentFlowData;
  const selectedFile = uploadedFiles?.[id]?.file;
  const exceedsSize = errorMessage === 'sizeError';

  const isUploading = progress > 0 && progress < 100;

  return (
    <ColumnView id={'upload'} alignItems={'flex-start'} paddingHorizontal={20} paddingVertical={10} gap={16}>
      <RowView justifyContent={'flex-start'}>
        <div className={`item-title item-label ${shouldShowError ? 'warning' : ''}`}>{name}</div>
        {required && <div className="text-fail text-md pl-4">*</div>}
      </RowView>

      <RowView justifyContent={'space-between'}>
        <RowView justifyContent={'flex-start'} gap={10}>
          <img
            className={`document-img ${shouldShowError ? 'warning' : ''}`}
            src={selectedFile ? URL.createObjectURL(selectedFile) : PDFIncomplete}
          />

          {isUploading ? (
            <RowView justifyContent={'flex-start'} width={'100%'} gap={10} paddingRight={10}>
              <div className="upload-progress-bar-border w-full">
                <div className="upload-progress-bar-container">
                  <div className="upload-progress-bar" style={{ width: progress + '%' }} />
                </div>
              </div>
            </RowView>
          ) : (
            <p className={`message ${errorMessage ? 'warning' : ''}`}>
              {errorMessage?.length > 0
                ? I18n.t(
                    `flow:components.uploadFile.${
                      exceedsSize
                        ? 'exceedsSize'
                        : errorMessage === 'typeNotSupported'
                        ? 'typeNotSupported'
                        : 'errorUploading'
                    }`
                  )
                : valueName}
            </p>
          )}
        </RowView>

        <RowView justifyContent={'flex-end'} width={'auto'}>
          <Button.OutlineSmall
            processing={loadingImg}
            width={'85px'}
            onClick={onButtonPress.bind(null, {
              component_type,
              fieldId,
              file,
              id,
              onButtonClick,
              transient,
              updateTransientProps,
              updateValue
            })}>
            {valueName ? <Refresh className={'icon'} /> : I18n.t('flow:components.uploadFile.upload')}
          </Button.OutlineSmall>
        </RowView>

        <input
          ref={uploadInputRef}
          type="file"
          className="display-none"
          onChange={handleUpload.bind(null, {
            component_type,
            fieldId,
            file,
            id,
            isOffline,
            setErrorMessage,
            setLoadingImg,
            setProgress,
            transient,
            updateTransientProps,
            updateValue
          })}
          /*
          We need to accept application/pdf along with image
          to enable camera capture functionality on Android devices
          */
          //eslint-disable-next-line
          accept="image/jpg, image/jpeg, image/png, image/gif, application/pdf"
        />
      </RowView>
    </ColumnView>
  );
};

Upload.propTypes = {
  component_type: PropTypes.string,
  componentFlowData: PropTypes.object,
  fields: PropTypes.array,
  id: PropTypes.string,
  isOffline: PropTypes.bool,
  name: PropTypes.string,
  properties: PropTypes.array,
  required: PropTypes.bool,
  shouldShowError: PropTypes.bool,
  transient: PropTypes.object,
  updateTransientProps: PropTypes.func,
  updateValue: PropTypes.func
};

export default Upload;
